import "../../../variables.css";
import React, { useMemo, useState } from "react";
import { Empty } from "antd";
import ReactApexChart from "react-apexcharts";
import { useAxisLabelFontStyle } from "../../../Utils/ChartStyle";
import GraphLayout from "../../GraphLayout";
import "./style.css";

const DynamicBarChart = ({
  graphData,
  lengthKeys,
  colourTheme = ["#000", "#fff"],
  graphTitle = "Undefined",
  selectTenByDefault = false,
  classNameCSS,
  dynamicColor,
  units = [],
}) => {
  const { axisLabelFontStyle, chartRef } = useAxisLabelFontStyle();
  const [filteredLabels, setFilteredLabels] = useState([]);
  const [series, setSeries] = useState([]);

  const filteredGraphData = useMemo(() => {
    if (!graphData || !lengthKeys) return graphData;
    const entries = Object.entries(graphData);
    const slicedEntries = entries.slice(0, lengthKeys + 1);
    return Object.fromEntries(slicedEntries);
  }, [graphData, lengthKeys]);

  const values = (graphData?.fuel || [])
    .map(Number)
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);

  const percentile = (vals, p) => {
    if (!vals.length) return 0;

    const idx = (p / 100) * (vals.length - 1);
    const lower = Math.floor(idx);
    const upper = Math.ceil(idx);
    const weight = idx - lower;

    return vals[lower] * (1 - weight) + vals[upper] * weight;
  };

  const q1 = percentile(values, 25);
  const q3 = percentile(values, 75);

  const counts = values.reduce(
    (acc, v) => {
      if (v < q1) acc.low++;
      else if (v > q3) acc.good++;
      else acc.mild++;
      return acc;
    },
    { low: 0, mild: 0, good: 0 }
  );
  const total = values.length || 1;

  const percentages = {
    low: +((counts.low / total) * 100).toFixed(2),
    mild: +((counts.mild / total) * 100).toFixed(2),
    good: +((counts.good / total) * 100).toFixed(2),
  };

  const getDynamicColor = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "#ccc";

    if (num < q1) return "#FB3748"; // low
    if (num > q3) return "#1FC16B"; // good
    return "#DFB400"; // mild
  };

  if (graphData?.labels?.length < 1 || graphData?.label?.length < 1) {
    return (
      <GraphLayout
        title={graphTitle}
        data={filteredGraphData}
        setFilteredLabels={setFilteredLabels}
        series={series}
        setSeries={setSeries}
        selectTenByDefault={selectTenByDefault}
        colourTheme={colourTheme}
        classNameCSS={classNameCSS}
      >
        <Empty
          style={{
            height: "300px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
          description={
            <span style={{ color: "#FB3748" }}>
              {`No ${graphTitle} data available`}
            </span>
          }
        />
      </GraphLayout>
    );
  }

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    colors: [
      ({ value, dataPointIndex, w }) =>
        getDynamicColor(value, dataPointIndex, w),
    ],
    fill: {
      type: "solid",
      colors: [
        ({ value, dataPointIndex, w }) =>
          getDynamicColor(value, dataPointIndex, w),
      ],
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        colors: ["var(--Parimitives-Charts-charts-Text)"],
        fontSize: "12px",
        fontWeight: 400,
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: axisLabelFontStyle,
      },
      axisBorder: {
        show: true,
        color: "var(--Parimitives-Charts-charts-strokes)",
      },
      axisTicks: {
        show: true,
        color: "var(--Parimitives-Charts-charts-strokes)",
      },
    },
    legend: {
      show: true,
      position: "top",
      fontSize: "12px",
      labels: {
        colors: "var(--Parimitives-Charts-charts-Text)",
        style: {
          fontFamily: "Inter",
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },

    plotOptions: {
      bar: {
        borderRadius: 8,
        dataLabels: {
          position: "top",
        },
      },
    },
    xaxis: {
      categories: filteredLabels,
      labels: {
        show: true,
        style: axisLabelFontStyle,
      },
      axisBorder: {
        show: true,
        color: "var(--Parimitives-Charts-charts-strokes)",
      },
      axisTicks: {
        show: true,
        color: "var(--Parimitives-Charts-charts-strokes)",
      },
    },
    grid: {
      borderColor: "var(--Parimitives-Charts-charts-strokes)",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    tooltip: {
      enabled: true,
      y: units.map((unit) => ({
        formatter: function (val) {
          if (val === undefined || val === null) return "";

          if (unit === "$") {
            return `${unit} ${val}`;
          }

          return `${val} ${unit}`;
        },
      })),
    },
  };

  return (
    <GraphLayout
      title={graphTitle}
      data={filteredGraphData}
      setFilteredLabels={setFilteredLabels}
      series={series}
      setSeries={setSeries}
      selectTenByDefault={selectTenByDefault}
      colourTheme={colourTheme}
      classNameCSS={classNameCSS}
    >
      <div className="special-styles-dynamic-bar-chart" ref={chartRef}>
        <ReactApexChart
          height={300}
          options={options}
          series={series}
          type="bar"
        />
        <div className="legend">
          <span className="legend-item">
            <span
              className="legend-color"
              style={{ background: "#1FC16B" }}
            ></span>
            Good
          </span>
          <span className="legend-item">
            <span
              className="legend-color"
              style={{ background: "#DFB400" }}
            ></span>
            Mild
          </span>
          <span className="legend-item">
            <span
              className="legend-color"
              style={{ background: "#FB3748" }}
            ></span>
            Low
          </span>
        </div>
      </div>
    </GraphLayout>
  );
};

export default DynamicBarChart;
